import { createFileRoute } from "@tanstack/react-router";
import { BRAND_NETWORK_URLS } from "@/data/brand-network";
import { buildHead } from "@/lib/seo-head";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { Section, SectionHeader, FeatureGrid, FAQ, CTABand, StatRow } from "@/components/site/Blocks";
import { TLDR } from "@/components/site/SeoBlocks";

import { products } from "@/data/products";
import { states } from "@/data/states";
import { SITE } from "@/data/site";
import { Link } from "@tanstack/react-router";
import heroAsset from "@/assets/hero-dscr-investment-property.jpg.asset.json";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { QuickQuoteWidget } from "@/components/site/QuickQuoteWidget";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import { listPublishedPosts, type PublicBlogPost } from "@/lib/blog-public.functions";

const faqItems = [
  {
    q: "What is a DSCR loan and how does it work?",
    a: "A DSCR (Debt Service Coverage Ratio) loan is a business-purpose investment property loan that qualifies you based on the property's cash flow rather than your personal income. Lenders divide the property's gross monthly rent by its total monthly debt payment (PITIA: principal, interest, taxes, insurance, and HOA fees). If the resulting ratio meets the program minimum, the deal can qualify without traditional income documentation.",
  },
  {
    q: "Who qualifies for a DSCR loan?",
    a: "Real estate investors, LLCs, partnerships, and corporations who own or are purchasing non-owner-occupied rental properties typically qualify. Borrowers are qualified on the property's projected or actual rental income, not personal pay stubs or W-2s. Personal credit, liquid reserves, and property type also factor into approval.",
  },
  {
    q: "Do DSCR loans require tax returns or personal income verification?",
    a: "No. DSCR loans are underwritten as business-purpose loans on investment properties, so personal tax returns, W-2s, and pay stubs are generally not required. The loan approval focuses on the property's ability to generate rental income and the borrower's overall credit and assets.",
  },
  {
    q: "What is the minimum DSCR required?",
    a: "Minimum DSCR requirements vary by program and can start as low as 0.75 in some cases, though most programs prefer a DSCR of 1.0 or higher. A higher DSCR often means stronger loan terms and better pricing. We can review your specific scenario and recommend the right program.",
  },
  {
    q: "Can I close a DSCR loan in an LLC or business entity?",
    a: "Yes. LLC, LP, S-Corporation, C-Corporation, and Series LLC vesting are all standard on eligible programs at no additional rate premium. Closing in a business entity helps protect personal assets and is a common structure for real estate investors.",
  },
  {
    q: "What types of properties can be financed with a DSCR loan?",
    a: "Eligible properties include 1–8 unit residential rentals, single-family homes, condominiums, townhomes, multi-unit buildings, and select portfolio properties. Short-term rentals and Airbnb properties may also qualify under specific investor programs. Commercial and owner-occupied properties are not eligible.",
  },
  {
    q: "What is the maximum DSCR loan amount and LTV?",
    a: "Loan amounts typically range from $100,000 up to $5,000,000, with higher amounts available on portfolio and jumbo DSCR programs. Maximum LTV varies by program and can go up to 80% on purchase and refinance transactions, with lower LTVs for cash-out refinances.",
  },
  {
    q: "How is the DSCR calculated on a rental property?",
    a: "DSCR is calculated by dividing the property's gross monthly rent by the total monthly housing payment. The payment includes principal, interest, property taxes, homeowners insurance, and HOA dues (PITIA). For example, if a property rents for $2,000 per month and the PITIA is $1,600, the DSCR is 1.25.",
  },
  {
    q: "How long does it take to close a DSCR loan?",
    a: "Most DSCR loans close in 15–21 business days from a complete application. The biggest variable is typically the appraisal and property inspection timeline. Rush closings may be possible in select scenarios when all documentation is submitted quickly.",
  },
  {
    q: "In which states are DSCR loans available?",
    a: "Simply Approved Mortgages is licensed in Florida and Colorado and offers DSCR and business-purpose investor loans as LLC business-purpose transactions in additional states where permitted by law. Contact us to confirm availability for your specific property state.",
  },
  {
    q: "Can I use a DSCR loan to refinance an investment property?",
    a: "Yes. DSCR loans can be used for rate-and-term refinances and cash-out refinances on existing investment properties. Cash-out proceeds can be used to acquire new properties, fund renovations, or reinvest in your portfolio.",
  },
  {
    q: "Are DSCR loans available for foreign national investors?",
    a: "Yes. Foreign National investor loans are available to non-U.S. citizens and permanent residents who want to invest in U.S. rental real estate. These programs use the property's rental income for qualification and do not require U.S. tax returns or traditional U.S. credit reports.",
  },
  {
    q: "What loan terms and interest rates are available?",
    a: "DSCR loans are available as 30-year fixed, adjustable-rate (ARM), and interest-only options. Rates depend on credit profile, DSCR, LTV, property type, and loan program. Interest-only options can improve cash flow during the early years of ownership.",
  },
  {
    q: "Can a DSCR loan be used for a short-term rental or Airbnb property?",
    a: "Yes. Airbnb and short-term rental properties may qualify under dedicated investor programs. Qualifying income may be based on short-term rental history, market rents, or projected income depending on the program and property experience.",
  },
  {
    q: "How do I apply for a DSCR loan with Simply Approved Mortgages?",
    a: "Start by completing our online application or contacting our lending team. We will review the property address, purchase price or estimated value, monthly rental income, and borrowing entity. Once we have the basics, we can price the deal and deliver an initial scenario review (formal pricing after underwriting), often the same business day.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

export const Route = createFileRoute("/")({
  head: () => {
    const url = "/";
    const base = buildHead({
      title: "DSCR Loans for Investors | Simply Approved Mortgages",
      description:
        "DSCR loans qualified on rental cash flow—no tax returns, LLC vesting welcomed. Licensed in FL & CO; business-purpose loans in additional states. Same-day scenario review on complete applications.",
      url,
      keywords:
        "DSCR loans, DSCR loan requirements, DSCR loan calculator, DSCR loans Florida, DSCR loans Colorado, no tax return investment property loans, LLC rental property loan, business-purpose mortgage, Airbnb DSCR loan, short-term rental loan, foreign national mortgage, fix and flip loan, ground-up construction loan, Section 8 rental loan, portfolio rental loan",
      image: heroAsset.url,
      imageAlt: "DSCR-financed brick multi-family rental investment property at dusk",
      breadcrumbs: [{ name: "Home", url }],
      service: { name: "DSCR & Business-Purpose Investment Property Lending" },
      faqs: faqItems,
      speakableSelectors: [".page-hero", ".faq"],
      extraJsonLd: [
        // WebSite schema is emitted sitewide from __root.tsx; no SearchAction
        // here because the site has no /search results page to point at.

        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "/#organization",
          name: "Simply Approved Mortgages",
          url: "/",
          logo: "/og-image.jpg",
          sameAs: [
            "https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2620881",
            ...BRAND_NETWORK_URLS,
          ],
        },
      ],
    });
    return base;
  },
  loader: async () => {
    try {
      return { latest: (await listPublishedPosts({ data: { limit: 4 } })) as PublicBlogPost[] };
    } catch {
      return { latest: [] as PublicBlogPost[] };
    }
  },
  component: Home,
});


function Home() {
  const { latest } = Route.useLoaderData();
  return (
    <SiteShell productLabel="Investor">
      <PageHero
        eyebrow="Simply Approved Mortgages · DSCR Lending Desk"
        title="DSCR Loans & Investment Property Financing for Real Estate Investors"
        subtitle="Qualify using property cash flow—not personal income or tax returns. Eight business-purpose lending solutions: DSCR, Foreign National, Ground-Up Construction, Fix & Flip, Bridge, Airbnb/Short-Term Rental, Section 8, and Portfolio Rental Loans. LTVs up to 80% on eligible DSCR programs, with no tax returns or personal income documentation required. Licensed in Florida and Colorado, and lending across our full service area as LLC business-purpose transactions where permitted."
        primaryLabel="Start My Approval"
        image={{
          src: heroAsset.url,
          alt: "DSCR-financed brick multi-family rental investment property at dusk — Simply Approved Mortgages",
        }}
      />
      <PageEssentials hideTldr />
      <LiveDscrRateSection
        strategyCards
        id="live-rates"
        eyebrow={"Live pricing · DSCR"}
        heading={"Today's DSCR rates at a glance"}
        paragraphs={[
          "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property — rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/rates", label: "See today's investor rate bands" },
        ]}
      />

      <Section>
        <TLDR
          id="tldr"
          points={[
            "DSCR loans qualify on the property's rent-to-PITIA ratio, not W-2s or tax returns.",
            "Close in an LLC, LP, or S-Corp with no rate premium on eligible programs.",
            "Programs range from 30-year fixed to interest-only with LTVs up to 80% and loans to $5M.",
            "Licensed lender in Florida and Colorado; LLC business-purpose loans available across our multi-state service area where permitted.",
            "Eligible DSCR programs go up to 80% LTV with no tax returns or personal income documentation — qualification is based on the property's rent, credit, and reserves.",
            "Initial scenario review usually same day once we have your completed application, credit authorization, and property/entity documents.",
          ]}
        />
      </Section>



      <Section>
        <p className="text-center font-display text-xl italic text-brand md:text-2xl">
          {SITE.mission}.
        </p>
        <SectionHeader
          title="Why Real Estate Investors Choose Simply Approved Mortgages"
          center
        />
        <div className="mt-10">
          <StatRow
            items={[
              { label: "Licensed in", value: "FL · CO" },
              { label: "Service area", value: `${states.length} states + DC` },
              { label: "Max LTV (DSCR)", value: "Up to 80%" },
              { label: "Income docs", value: "None required" },
              { label: "Investor programs", value: "8" },
              { label: "Min DSCR", value: "0.75" },
              { label: "Max loan", value: "$5M" },
              { label: "Vesting", value: "LLC / LP / Corp" },
            ]}
          />
          <p className="mt-3 text-xs text-muted-foreground">
            Maximum LTV, minimum DSCR, and loan amounts shown are program maximums on eligible business-purpose DSCR loans. Actual terms depend on credit, property type, reserves, appraisal, and underwriting approval. No tax returns or personal income documentation are required on eligible DSCR programs; credit, reserves, and property documentation still apply.
          </p>
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Coverage"
          title={`DSCR lending in ${states.length} states plus Washington, DC`}
          intro="Simply Approved Mortgages LLC is a mortgage broker, not a direct lender, and arranges residential mortgage loans in Florida and Colorado. Everywhere else in our service area, the same DSCR and business-purpose programs are available to LLCs, LPs, and corporations as business-purpose transactions where state law permits — so investors outside FL and CO can still close with us."
        />
        <div className="mt-8 flex flex-wrap gap-2">
          {states.map((s) => (
            <Link
              key={s.slug}
              to="/dscr-loans/$state"
              params={{ state: s.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-brand hover:text-brand"
            >
              {s.abbrev}
            </Link>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Not licensed for consumer mortgage lending outside Florida and Colorado. Availability varies by state and property type.{" "}
          <Link to="/where-we-lend" className="font-semibold text-brand underline-offset-4 hover:underline">
            See full state coverage
          </Link>
          .
        </p>
      </Section>

      <div className="bg-muted/40">
        <Section>
          <SectionHeader eyebrow="Investor Programs" title="One lender. Every business-purpose deal." center />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}` as string}
                className="group rounded-xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand hover:shadow-md"
              >
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">{p.shortName}</div>
                <div className="mt-2 font-display text-xl font-bold text-foreground group-hover:text-brand">{p.name}</div>
                <p className="mt-2 text-sm text-muted-foreground">{p.tagline}</p>
                <div className="mt-4 text-xs text-muted-foreground">
                  {p.minLoan} – {p.maxLoan} · LTV {p.ltvMax}
                </div>
              </Link>
            ))}
          </div>
        </Section>
      </div>

      <Section>
        <SectionHeader
          title="How DSCR Loans Work"
          intro="Qualify based on your property's rental income—not your personal income. With a DSCR loan, lenders compare the property's gross monthly rent to its monthly housing expenses (PITIA) to determine eligibility."
        />
        <div className="mt-10">
          <div className="mb-10 rounded-xl border border-border bg-card p-8 text-center shadow-sm">
            <div className="font-display text-2xl font-bold text-foreground md:text-3xl">
              Rent ÷ PITIA = Your DSCR
            </div>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              If your property's rental income meets or exceeds the program's minimum DSCR requirement, you may qualify without providing traditional income documentation.
            </p>
          </div>
          <FeatureGrid
            items={[
              { title: "No Tax Returns Required", body: "Business-purpose underwriting means no personal income verification, W-2s, tax returns, or pay stubs for eligible DSCR loans." },
              { title: "Close in Your Business Entity", body: "Purchase or refinance in the name of your LLC, LP, or S-Corporation with no additional rate premium on eligible programs." },
              { title: "Unlimited Investment Properties", body: "Continue growing your portfolio with no set limit on the number of financed investment properties." },
              { title: "Licensed in FL & CO — Business-Purpose Lending Across 37 States + DC", body: "Licensed mortgage lending in Florida and Colorado. In the rest of our service area, the same DSCR programs are available to LLCs, LPs, and corporations as business-purpose loans where state law permits." },
            ]}
          />
        </div>
      </Section>

      {/* Why DSCR Loans */}
      <div className="bg-muted/40">
        <Section>
          <SectionHeader
            eyebrow="Why investors choose DSCR"
            title="Why DSCR Loans Are Built for Real Estate Investors"
            intro="DSCR financing removes the personal-income bottleneck that stops most investors from scaling. You qualify on the deal, close in your entity, and stack properties without traditional debt-to-income limits."
          />
          <FeatureGrid
            items={[
              { title: "Qualify on the property, not your paystub", body: "Underwriting focuses on rent vs PITIA. Self-employed investors, 1099 earners, and portfolio landlords aren't penalized for tax-optimized returns." },
              { title: "Scale without DTI caps", body: "Conventional loans cap you at ~10 financed properties and stack every mortgage into your DTI. DSCR treats each rental as its own cash-flowing business — no ceiling on financed doors." },
              { title: "Close in an LLC for asset protection", body: "Vest in an LLC, LP, or S-Corp at no rate premium. Keep rentals off your personal credit and separate liability from your personal balance sheet." },
              { title: "Faster, cleaner closings", body: "No tax returns, W-2s, or employment verification. Most files close in 15–21 business days with minimal back-and-forth on income docs." },
              { title: "Cash-out to fund the next deal", body: "Pull tax-free equity out of stabilized rentals to buy the next property, fund rehab, or season reserves — without touching your primary residence." },
              { title: "Works for every rental strategy", body: "Long-term SFR, Section 8, mid-term corporate, and short-term Airbnb properties all qualify under program-specific DSCR guidelines." },
            ]}
          />
        </Section>
      </div>

      <section data-inline-cta="">
        <div className="container mx-auto max-w-7xl px-4">
          <ProductReportCtaBanner productKey="dscr" variant="inline" />
        </div>
      </section>

      {/* How to Build a Real Estate Empire */}
      <Section>
        <SectionHeader
          eyebrow="The blueprint"
          title="How to Build a Real Estate Empire With DSCR Financing"
          intro="A repeatable 5-step playbook serious investors use to go from one rental to a self-sustaining portfolio — funded by the properties themselves."
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {[
            { n: "01", t: "Buy your first cash-flowing rental", b: "Target a market with rent-to-price ratios that clear a 1.0+ DSCR. Close in an LLC with 20–25% down using a DSCR purchase loan." },
            { n: "02", t: "Stabilize and season", b: "Place a qualified tenant (long-term, Section 8, or STR), document 3–12 months of rent history, and let the property appreciate." },
            { n: "03", t: "Cash-out refinance", b: "Once equity has grown, pull tax-free capital out with a DSCR cash-out refi up to 75–80% LTV. The rent still services the new payment." },
            { n: "04", t: "Redeploy into the next door", b: "Use the cash-out proceeds as the down payment on property #2. Same DSCR playbook — no personal income re-verification required." },
            { n: "05", t: "Repeat and diversify", b: "Layer in Airbnb, Section 8, and multi-unit rentals across the states where we lend. Each new door adds cash flow without capping your borrowing power." },
          ].map((s) => (
            <div key={s.n} className="relative rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="font-display text-3xl font-bold text-brand-gold">{s.n}</div>
              <div className="mt-2 font-display text-lg font-bold text-foreground">{s.t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.b}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-xl border border-brand-gold/40 bg-brand-gold/10 p-6 text-center">
          <div className="font-display text-lg font-bold text-foreground">The BRRRR loop: Buy → Rehab → Rent → Refinance → Repeat</div>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
            Pair a short-term bridge or fix-&-flip loan with a DSCR refinance exit to recycle the same capital into multiple properties per year.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="inline-flex items-center justify-center rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold text-primary hover:bg-brand-gold-hover">Start My Approval</a>
            <a href="/brrrr-calculator" className="inline-flex items-center justify-center rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:border-brand hover:text-brand">Run the BRRRR Calculator</a>
          </div>
        </div>
      </Section>

      <Section>
        <QuickQuoteWidget sourcePage="home" />
      </Section>

      {/* Types of Rental Income */}
      <div className="bg-muted/40">
        <Section>
          <SectionHeader
            eyebrow="Rental strategies"
            title="Types of Rental Income DSCR Loans Support"
            intro="Every income strategy has a different risk, yield, and management profile. DSCR programs underwrite all three — pick the one that fits your market and goals."
          />
          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            {[
              {
                tag: "Long-term SFR",
                title: "Traditional single-family rental",
                yieldNote: "Yield: moderate · Effort: low",
                body: "12-month leases to qualified tenants. The bedrock of most rental portfolios — predictable cash flow, low turnover, and the most straightforward DSCR underwriting.",
                pros: ["Stable monthly rent", "Lowest management overhead", "Widest lender appetite & best pricing", "Easy to scale across markets"],
                cons: ["Lower gross yield vs Airbnb", "Rent growth capped by local market", "Vacancy hits full month of income"],
              },
              {
                tag: "Section 8",
                title: "Section 8 / Housing Choice Voucher",
                yieldNote: "Yield: moderate · Effort: low–medium",
                body: "The Public Housing Authority (PHA) pays a large portion of rent directly to you via HAP contract at the local HUD Fair Market Rent. Government-backed income with waitlist demand.",
                pros: ["Direct-deposit rent from the PHA", "Below-market vacancy — long waitlists", "24-month HAP contracts", "Rents indexed to HUD FMR each year"],
                cons: ["Annual PHA inspection required", "Slower initial lease-up (30–60 days)", "Rent capped at FMR / payment standard"],
              },
              {
                tag: "Airbnb / STR",
                title: "Short-term rental (Airbnb / VRBO)",
                yieldNote: "Yield: high · Effort: high",
                body: "Nightly bookings priced dynamically by season and demand. Highest gross revenue potential but the most operationally intensive — permits, cleaning, and platform management all matter.",
                pros: ["Highest gross revenue per door", "Peak-season and event pricing upside", "Personal-use flexibility", "Faster path to 1.25+ DSCR in the right market"],
                cons: ["Local STR ordinances & HOA restrictions", "Cleaning, turnover, and platform fees", "Seasonal occupancy swings", "Tighter DSCR programs (STR-specific)"],
              },
            ].map((r) => (
              <article key={r.tag} className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">{r.tag}</div>
                <h3 className="mt-2 font-display text-xl font-bold text-foreground">{r.title}</h3>
                <div className="mt-1 text-xs text-muted-foreground">{r.yieldNote}</div>
                <p className="mt-3 text-sm text-muted-foreground">{r.body}</p>
                <div className="mt-4 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-foreground">Pros</div>
                    <ul className="mt-2 space-y-1.5">
                      {r.pros.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <span className="mt-0.5 text-brand-gold">✔</span>{p}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-foreground">Watch-outs</div>
                    <ul className="mt-2 space-y-1.5">
                      {r.cons.map((c) => (
                        <li key={c} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <span className="mt-0.5 text-brand">•</span>{c}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-sm text-muted-foreground">
            Not sure which strategy fits your market? A licensed loan officer can model long-term, Section 8, and Airbnb income on the same property so you can compare projected DSCR before you commit.
          </p>
          <div className="mt-6 flex justify-center">
            <a href="/apply" className="inline-flex items-center justify-center rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold text-primary hover:bg-brand-gold-hover">Start My Approval</a>
          </div>
        </Section>
      </div>


      <div className="bg-muted/40">
        <Section>
          <SectionHeader
            eyebrow="Initial Scenario Review on Complete Applications"
            title="Get a Personalized DSCR Loan Quote"
            intro="Complete the short pre-qualification form and share your scenario details. A licensed loan officer reviews the scenario, sizes the loan, and provides an initial scenario review — often the same business day. Formal pricing, LTV, and terms are issued after underwriting."

          />
          <div className="mt-10 grid items-start gap-8 lg:grid-cols-2">
            <div className="text-sm">
              <p className="font-display font-semibold text-foreground">Simply send us:</p>
              <ul className="mt-3 space-y-3">
                {[
                  "Property address",
                  "Purchase price (or estimated value)",
                  "Monthly rental income",
                ].map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-gold" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <p className="font-display font-semibold text-foreground">Financing Options Available</p>
                <ul className="mt-4 space-y-4">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-brand-gold">✔</span>
                    <span>
                      <span className="font-semibold text-foreground">30-Year Fixed, Adjustable-Rate (ARM) & Interest-Only Loans</span>
                      {" "}— Choose the loan structure that best supports your investment strategy.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-brand-gold">✔</span>
                    <span>
                      <span className="font-semibold text-foreground">Purchase, Refinance & Cash-Out Refinancing</span>
                      {" "}— Finance new acquisitions or unlock equity from your existing portfolio.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-brand-gold">✔</span>
                    <span>
                      <span className="font-semibold text-foreground">1–8 Unit Residential & Portfolio Financing</span>
                      {" "}— Solutions for single rental properties, multi-unit investments, and portfolio loans.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 text-brand-gold">✔</span>
                    <span>
                      <span className="font-semibold text-foreground">Specialized Investor Programs</span>
                      {" "}— Access Foreign National, Ground-Up Construction, Bridge, Fix & Flip, and other business-purpose lending solutions through one experienced lending team.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Speed</div>
                <div className="mt-1 font-display text-lg font-bold text-foreground">15–21 day close</div>
                <p className="mt-1 text-xs text-muted-foreground">Most DSCR files close inside three weeks from a complete application.</p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Vesting</div>
                <div className="mt-1 font-display text-lg font-bold text-foreground">LLC · LP · S-Corp</div>
                <p className="mt-1 text-xs text-muted-foreground">Entity vesting at no rate premium on eligible programs.</p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Min DSCR</div>
                <div className="mt-1 font-display text-lg font-bold text-foreground">0.75</div>
                <p className="mt-1 text-xs text-muted-foreground">Sub-1.0 DSCR programs available with compensating factors.</p>
              </div>
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">Docs</div>
                <div className="mt-1 font-display text-lg font-bold text-foreground">No tax returns</div>
                <p className="mt-1 text-xs text-muted-foreground">Business-purpose underwriting—no W-2s, pay stubs, or 1040s required.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="/apply"
              className="inline-flex items-center justify-center rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold text-primary hover:bg-brand-gold-hover"
            >Start My Approval</a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-md border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:border-brand hover:text-brand"
            >
              Speak With a Licensed Loan Officer
            </a>
          </div>
        </Section>
      </div>


      <Section id="lead-form">
        <CTABand
          title="Get your DSCR scenario review"
          body="Complete the short pre-qualification form and share your scenario details. A licensed loan officer responds with an initial scenario review — often the same business day. Formal pricing and terms follow underwriting."

          cta="Get Your Personalized Rate Quote"
        />
      </Section>



      <section data-inline-cta="">
        <div className="container mx-auto max-w-7xl px-4">
          <ProductReportCtaBanner productKey="dscr" variant="inline" />
        </div>
      </section>

      <Section>
        <SectionHeader eyebrow="Where we lend" title="Investor-friendly lending in Florida, Colorado, and LLC-eligible states" />
        <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {states.map((s) => (
            <Link
              key={s.slug}
              to="/dscr-loans/$state"
              params={{ state: s.slug }}
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:border-brand hover:text-brand"
            >
              {s.name}
            </Link>
          ))}
        </div>
      </Section>

      {latest.length > 0 && (
        <Section>
          <SectionHeader
            eyebrow="Investor guides"
            title="Latest DSCR and Rental Financing Guides"
            intro="New articles on qualifying, structuring, and refinancing investment property."
          />
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {latest.map((p) => (
              <Link
                key={p.slug}
                to="/blog/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col rounded-xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand"
              >
                {p.category && <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold">{p.category}</div>}
                <div className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">{p.title}</div>
                {p.excerpt && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>}
                <div className="mt-auto pt-4 text-xs text-muted-foreground">{p.read_mins} min read</div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link to="/blog" className="font-semibold text-brand underline-offset-4 hover:underline">Browse all investor guides</Link>
          </div>
        </Section>
      )}

      <Section>
        <SectionHeader
          eyebrow="DSCR Loan FAQ"
          title="Frequently Asked Questions About DSCR Loans"
          intro="Get clear, investor-focused answers to the most common questions about DSCR loans, qualification, property types, LLC vesting, and our lending process."
          center
        />
        <div className="mt-10">
          <FAQ items={faqItems} />
        </div>
      </Section>

      <CTABand
        title="Ready to Price Your Next DSCR Loan?"
        body="Submit your application with property address, purchase price, rental income, borrowing entity, credit authorization, and supporting documents. We'll review your scenario and deliver an initial scenario review — often the same day. Formal pricing and terms follow full underwriting."
        cta="Get Your Personalized Rate Quote"
      />

    <PageOutro />
      </SiteShell>
  );
}
